import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircle2,
  Clock3,
  FileText,
  MapPin,
  Truck,
} from 'lucide-react';
import { createIdempotencyKey } from '../lib/idempotency';
import { formatDateTime, formatStatus } from '../lib/formatters';
import { useSubmissionLock } from '../hooks/useSubmissionLock';
import {
  acceptContractorJob,
  declineContractorJob,
  fetchContractorJob,
  openContractorJobAttachment,
  proposeContractorDate,
  submitContractorProof,
  submitContractorQuote,
  updateContractorPreference,
  updateContractorProgress,
} from '../api/jobs';

const STEPS = ['Quote', 'Scheduling', 'Work', 'Completion'];

function currentStep(job) {
  const status = job?.status;
  if (['ASSIGNED', 'SMS_SENT', 'AWAITING_CONTRACTOR_RESPONSE', 'WORKER_DECLINED', 'WORKER_ACCEPTED', 'AWAITING_QUOTE', 'QUOTE_SUBMITTED', 'QUOTE_DECLINED', 'QUOTE_REVISION_REQUESTED'].includes(status)) return 'Quote';
  if (['QUOTE_ACCEPTED', 'SCHEDULING', 'DATE_PROPOSED', 'AWAITING_LANDLORD_CONFIRMATION', 'AWAITING_TENANT_CONFIRMATION', 'APPOINTMENT_REJECTED', 'SCHEDULED'].includes(status)) return 'Scheduling';
  if (['REMINDER_SENT', 'ON_MY_WAY', 'ARRIVED', 'WORK_STARTED', 'WORK_FINISHED'].includes(status)) return 'Work';
  return 'Completion';
}

function fileNames(files) {
  const list = Array.isArray(files) ? files : files ? [files] : [];
  return list.map((file) => file?.name).filter(Boolean).join(', ');
}

function progressAction(job) {
  if (['SCHEDULED', 'REMINDER_SENT'].includes(job?.status)) {
    return { action: 'ON_MY_WAY', label: 'Mark On the Way', icon: Truck };
  }
  if (job?.status === 'ON_MY_WAY') {
    return { action: 'ARRIVED', label: 'Mark Arrived', icon: MapPin };
  }
  if (job?.status === 'ARRIVED') {
    return { action: 'WORK_STARTED', label: 'Mark Work Started', icon: Clock3 };
  }
  if (job?.status === 'WORK_STARTED') {
    return { action: 'WORK_FINISHED', label: 'Mark Work Finished', icon: CheckCircle2 };
  }
  return null;
}

export default function ContractorJobPortal() {
  const { token } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { run: runWithLock, isPending } = useSubmissionLock();
  const [activeDecision, setActiveDecision] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [quote, setQuote] = useState({
    quoteAmount: '',
    quoteNotes: '',
    estimatedDuration: '',
    materialsIncluded: false,
    attendanceWindow: '',
    quoteFile: null,
  });
  const [scheduleProposal, setScheduleProposal] = useState({
    proposedDate: '',
    proposedTime: '',
    scheduledEndDate: '',
    scheduledEndTime: '',
    notes: '',
    accessWith: '',
  });
  const [proof, setProof] = useState({
    completionNotes: '',
    invoiceReference: '',
    invoiceNumber: '',
    invoiceAmount: '',
    materialsUsed: '',
    actualCompletedAt: '',
    followUpRecommendation: '',
    additionalWorkRequired: false,
    proofFiles: [],
    beforePhotos: [],
    afterPhotos: [],
    invoiceFile: null,
    supportingDocuments: [],
  });
  const [preference, setPreference] = useState({
    futureJobNotifications: false,
    serviceArea: '',
  });

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');

    fetchContractorJob(token)
      .then((data) => {
        if (!mounted) return;
        setJob(data);
        setScheduleProposal((prev) => ({
          ...prev,
          accessWith: data?.accessRequirement || data?.schedule?.accessWith || '',
        }));
        setPreference({
          futureJobNotifications: Boolean(data?.contractorPreference?.futureJobNotifications),
          serviceArea: data?.contractorPreference?.serviceArea || '',
        });
      })
      .catch((err) => {
        console.error(err);
        if (!mounted) return;
        const status = err?.response?.status;
        const code = err?.response?.data?.code;
        if (status === 410 || code === 'TOKEN_EXPIRED') {
          setError('This job link has expired. Ask the landlord to send a fresh SMS link.');
        } else {
          setError('This job link is invalid. Check the SMS link or ask the landlord to resend it.');
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  const decisionBusy = isPending('job-decision');
  const quoteBusy = isPending('quote');
  const scheduleBusy = isPending('schedule');
  const progressBusy = isPending('progress');
  const proofBusy = isPending('proof');
  const preferenceBusy = isPending('preference');

  const runAction = async (key, action) => {
    await runWithLock(key, async () => {
      setError('');
      try {
        const updated = await action();
        setJob(updated);
      } catch (err) {
        console.error(err);
        setError(err?.response?.data?.error || 'Unable to update this job right now.');
      }
    });
  };

  const updateFileList = (field, fileList) => {
    setProof((prev) => ({ ...prev, [field]: Array.from(fileList || []) }));
  };

  const openAttachment = async (attachmentId) => {
    try {
      const url = await openContractorJobAttachment(token, attachmentId);
      if (url) window.open(url, '_blank', 'noopener');
    } catch (err) {
      console.error(err);
      setError('Unable to open that file right now.');
    }
  };

  const submitAcceptance = () => {
    runAction('job-decision', async () => {
      setActiveDecision('accept');
      try {
        return await acceptContractorJob(token, {
          idempotencyKey: createIdempotencyKey('contractor-accept'),
        });
      } finally {
        setActiveDecision('');
      }
    }
    );
  };

  const declineJob = () => {
    runAction('job-decision', async () => {
      setActiveDecision('decline');
      try {
        return await declineContractorJob(token, declineReason.trim(), {
          idempotencyKey: createIdempotencyKey('contractor-decline'),
        });
      } finally {
        setActiveDecision('');
      }
    }
    );
  };

  const submitQuote = (e) => {
    e.preventDefault();
    runAction('quote', () =>
      submitContractorQuote(token, {
        ...quote,
        idempotencyKey: createIdempotencyKey('contractor-quote'),
      })
    );
  };

  const submitDate = (e) => {
    e.preventDefault();
    const proposedDate = scheduleProposal.proposedDate && scheduleProposal.proposedTime
      ? `${scheduleProposal.proposedDate}T${scheduleProposal.proposedTime}`
      : '';
    const scheduledEndAt = scheduleProposal.scheduledEndTime
      ? `${scheduleProposal.scheduledEndDate || scheduleProposal.proposedDate}T${scheduleProposal.scheduledEndTime}`
      : '';

    runAction('schedule', () =>
      proposeContractorDate(token, {
        proposedDate,
        scheduledEndAt,
        notes: scheduleProposal.notes,
        accessWith: scheduleProposal.accessWith,
        idempotencyKey: createIdempotencyKey('contractor-appointment'),
      })
    );
  };

  const submitProof = (e) => {
    e.preventDefault();
    runAction('proof', () =>
      submitContractorProof(token, {
        ...proof,
        idempotencyKey: createIdempotencyKey('contractor-proof'),
      })
    );
  };

  const savePreference = (e) => {
    e.preventDefault();
    runAction('preference', async () => {
      const saved = await updateContractorPreference(token, preference);
      return { ...job, contractorPreference: saved };
    });
  };

  if (loading) return <div className="min-h-screen p-8 text-white">Loading job...</div>;
  if (error && !job) {
    return (
      <div className="min-h-screen text-white p-6 md:p-10">
        <div className="max-w-xl mx-auto bg-white/10 border border-white/10 rounded-xl p-6 backdrop-blur-md">
          <img
            src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/6824d581ae24f69d45e26536_beem%20logo%203.png"
            alt="Beem"
            className="w-28 mb-6"
          />
          <h1 className="text-2xl font-semibold mb-3">Job link unavailable</h1>
          <p className="text-white/70">{error}</p>
        </div>
      </div>
    );
  }

  const status = job?.status;
  const quoteApproved = job?.quote?.status === 'APPROVED' || status === 'QUOTE_ACCEPTED';
  const quoteNotRequired = job?.quote?.required === false || job?.quote?.status === 'NOT_REQUIRED';
  const canAccept = ['ASSIGNED', 'SMS_SENT', 'AWAITING_CONTRACTOR_RESPONSE'].includes(status);
  const canQuote = job?.quote?.required !== false && ['WORKER_ACCEPTED', 'AWAITING_QUOTE', 'QUOTE_DECLINED', 'QUOTE_REVISION_REQUESTED'].includes(status);
  const waitingQuoteDecision = status === 'QUOTE_SUBMITTED' || job?.quote?.status === 'PENDING_APPROVAL';
  const canSchedule = (quoteApproved || quoteNotRequired) && ['WORKER_ACCEPTED', 'QUOTE_ACCEPTED', 'SCHEDULING', 'APPOINTMENT_REJECTED'].includes(status);
  const waitingAppointment = ['DATE_PROPOSED', 'AWAITING_LANDLORD_CONFIRMATION', 'AWAITING_TENANT_CONFIRMATION'].includes(status);
  const nextProgress = progressAction(job);
  const canProof = ['WORK_FINISHED', 'FOLLOW_UP_REQUIRED', 'REOPENED'].includes(status);
  const completionWaiting = ['COMPLETION_SUBMITTED', 'PROOF_SUBMITTED', 'LANDLORD_APPROVED'].includes(status);
  const completed = ['COMPLETED', 'CLOSED'].includes(status);
  const step = currentStep(job);
  const stepIndex = Math.max(STEPS.indexOf(step), 0);
  const quoteWasRejected = ['QUOTE_DECLINED', 'QUOTE_REVISION_REQUESTED'].includes(status) || job?.quote?.status === 'REJECTED';

  const waitingText = (() => {
    if (waitingQuoteDecision) return 'Quote submitted. Waiting for landlord review.';
    if (waitingAppointment) {
      return status === 'AWAITING_TENANT_CONFIRMATION'
        ? 'Visit proposed. Waiting for the tenant to confirm.'
        : 'Visit proposed. Waiting for the landlord to confirm.';
    }
    if (completionWaiting) return 'Completion submitted. Waiting for landlord review.';
    if (completed) return 'This job is complete.';
    return 'No contractor action is needed right now.';
  })();

  return (
    <div className="min-h-screen text-white p-6 md:p-10">
      <div className="max-w-4xl mx-auto">
        <header className="mb-8">
          <img
            src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/6824d581ae24f69d45e26536_beem%20logo%203.png"
            alt="Beem"
            className="w-28 mb-6"
          />
          <p className="text-[#AF9B8A] text-sm uppercase tracking-wide">Beem job</p>
          <h1 className="text-3xl md:text-4xl mt-2">{job.title}</h1>
          <p className="text-white/60 mt-2">{formatStatus(status)}</p>
        </header>

        <ol className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
          {STEPS.map((item, index) => (
            <li
              key={item}
              className={`text-center text-xs rounded-lg border px-2 py-2 ${
                item === step
                  ? 'bg-[#AF9B8A] border-[#AF9B8A] text-white'
                  : index < stepIndex
                    ? 'bg-emerald-500/15 border-emerald-400/25 text-emerald-100'
                  : 'bg-white/5 border-white/10 text-white/60'
              }`}
            >
              {item}
            </li>
          ))}
        </ol>

        {error && <div className="mb-4 text-red-300">{error}</div>}

        <section className="bg-white/10 border border-white/10 rounded-xl p-6 backdrop-blur-md mb-6">
          <h2 className="text-xl font-semibold mb-4">{step}</h2>

          {step === 'Quote' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-white/80 bg-white/5 border border-white/10 rounded-lg p-4 mb-5">
              <p><strong>Property:</strong> {job.property?.address || job.property?.title || 'Property'}</p>
              <p><strong>Priority:</strong> {formatStatus(job.priority)}</p>
              <p><strong>Location:</strong> {job.roomLocation || 'Not specified'}</p>
              <p><strong>Access:</strong> {formatStatus(job.accessRequirement || job.schedule?.accessWith || 'NOT_STARTED')}</p>
              <p className="md:col-span-2"><strong>Description:</strong> {job.description || 'No description provided.'}</p>
              {job.accessNotes && <p className="md:col-span-2"><strong>Access notes:</strong> {job.accessNotes}</p>}
            </div>
          )}

          {canAccept && (
            <div className="space-y-4">
              <p className="text-white/75">Review the job details and choose whether to accept or decline this job.</p>
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Optional reason if declining"
                rows="2"
                className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-white"
              />
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  disabled={decisionBusy}
                  onClick={submitAcceptance}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#AF9B8A] text-white disabled:opacity-50"
                  aria-busy={decisionBusy}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {activeDecision === 'accept' ? 'Accepting...' : 'Accept Job'}
                </button>
                <button
                  type="button"
                  disabled={decisionBusy}
                  onClick={declineJob}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-white/10 border border-white/10 disabled:opacity-50"
                  aria-busy={decisionBusy}
                >
                  {activeDecision === 'decline' ? 'Declining...' : 'Decline Job'}
                </button>
              </div>
            </div>
          )}

          {canQuote && (
            <form onSubmit={submitQuote} className="grid grid-cols-1 md:grid-cols-2 gap-3" aria-busy={quoteBusy}>
              {quoteWasRejected && (
                <p className="md:col-span-2 text-amber-100 bg-amber-500/10 border border-amber-400/20 rounded-lg p-3">
                  {job.quote?.reviewNotes || 'The landlord asked for changes to this quote.'}
                </p>
              )}
              <input
                value={quote.quoteAmount}
                onChange={(e) => setQuote((prev) => ({ ...prev, quoteAmount: e.target.value }))}
                placeholder="Quote amount"
                required
                className="p-3 rounded-lg bg-white/5 border border-white/10 text-white"
              />
              <input
                value={quote.estimatedDuration}
                onChange={(e) => setQuote((prev) => ({ ...prev, estimatedDuration: e.target.value }))}
                placeholder="Estimated duration"
                className="p-3 rounded-lg bg-white/5 border border-white/10 text-white"
              />
              <input
                value={quote.attendanceWindow}
                onChange={(e) => setQuote((prev) => ({ ...prev, attendanceWindow: e.target.value }))}
                placeholder="Optional attendance window"
                className="p-3 rounded-lg bg-white/5 border border-white/10 text-white"
              />
              <label className="flex items-center gap-2 text-sm text-white/80">
                <input
                  type="checkbox"
                  checked={quote.materialsIncluded}
                  onChange={(e) => setQuote((prev) => ({ ...prev, materialsIncluded: e.target.checked }))}
                />
                Materials included
              </label>
              <textarea
                value={quote.quoteNotes}
                onChange={(e) => setQuote((prev) => ({ ...prev, quoteNotes: e.target.value }))}
                placeholder="Quote notes"
                rows="3"
                className="md:col-span-2 p-3 rounded-lg bg-white/5 border border-white/10 text-white"
              />
              <label className="md:col-span-2 p-3 rounded-lg bg-white/5 border border-white/10 text-white/80">
                <span className="block text-sm mb-2">Quote document or image</span>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                  onChange={(e) => setQuote((prev) => ({ ...prev, quoteFile: e.target.files?.[0] || null }))}
                  className="w-full text-sm"
                />
                {quote.quoteFile && <span className="block mt-2 text-xs text-white/50">{quote.quoteFile.name}</span>}
              </label>
              <button disabled={quoteBusy} className="md:col-span-2 px-4 py-2 rounded-lg bg-[#AF9B8A] text-white disabled:opacity-50">
                {quoteBusy ? 'Submitting quote...' : quoteWasRejected ? 'Submit Revised Quote' : 'Submit Quote'}
              </button>
            </form>
          )}

          {canSchedule && (
            <form onSubmit={submitDate} className="grid grid-cols-1 md:grid-cols-2 gap-3" aria-busy={scheduleBusy}>
              {status === 'APPOINTMENT_REJECTED' && (
                <p className="md:col-span-2 text-amber-100 bg-amber-500/10 border border-amber-400/20 rounded-lg p-3">
                  {job.schedule?.appointmentRejectionReason || 'The previous appointment proposal was rejected. Please propose another time.'}
                </p>
              )}
              <label className="block">
                <span className="block text-sm text-white/70 mb-1">Date</span>
                <input
                  type="date"
                  value={scheduleProposal.proposedDate}
                  onChange={(e) => setScheduleProposal((prev) => ({ ...prev, proposedDate: e.target.value }))}
                  required
                  className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-white"
                />
              </label>
              <label className="block">
                <span className="block text-sm text-white/70 mb-1">Start time</span>
                <input
                  type="time"
                  value={scheduleProposal.proposedTime}
                  onChange={(e) => setScheduleProposal((prev) => ({ ...prev, proposedTime: e.target.value }))}
                  required
                  className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-white"
                />
              </label>
              <label className="block">
                <span className="block text-sm text-white/70 mb-1">End date</span>
                <input
                  type="date"
                  value={scheduleProposal.scheduledEndDate}
                  onChange={(e) => setScheduleProposal((prev) => ({ ...prev, scheduledEndDate: e.target.value }))}
                  className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-white"
                />
              </label>
              <label className="block">
                <span className="block text-sm text-white/70 mb-1">End time</span>
                <input
                  type="time"
                  value={scheduleProposal.scheduledEndTime}
                  onChange={(e) => setScheduleProposal((prev) => ({ ...prev, scheduledEndTime: e.target.value }))}
                  className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-white"
                />
              </label>
              {scheduleProposal.proposedDate && scheduleProposal.proposedTime && (
                <div className="md:col-span-2 bg-white/5 border border-white/10 rounded-lg p-3">
                  <p className="text-sm text-white/60">Proposed appointment</p>
                  <p className="text-white mt-1">
                    {formatDateTime(`${scheduleProposal.proposedDate}T${scheduleProposal.proposedTime}`, 'Not set')}
                  </p>
                </div>
              )}
              <textarea
                value={scheduleProposal.notes}
                onChange={(e) => setScheduleProposal((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Scheduling notes"
                rows="2"
                className="md:col-span-2 p-3 rounded-lg bg-white/5 border border-white/10 text-white"
              />
              <button disabled={scheduleBusy} className="md:col-span-2 px-4 py-2 rounded-lg bg-[#AF9B8A] text-white disabled:opacity-50">
                {scheduleBusy ? 'Submitting...' : 'Submit Proposed Appointment'}
              </button>
            </form>
          )}

          {nextProgress && (
            <div className="space-y-4">
              <p className="text-white/75">
                Appointment confirmed for {formatDateTime(job.schedule?.scheduledStartAt || job.scheduledDate, 'the confirmed time')}.
              </p>
              <button
                type="button"
                disabled={progressBusy}
                onClick={() =>
                  runAction('progress', () =>
                    updateContractorProgress(token, nextProgress.action, null, {
                      idempotencyKey: createIdempotencyKey(`contractor-progress-${nextProgress.action.toLowerCase()}`),
                    })
                  )
                }
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#AF9B8A] text-white disabled:opacity-50"
                aria-busy={progressBusy}
              >
                <nextProgress.icon className="w-4 h-4" />
                {progressBusy ? (
                  nextProgress.action === 'WORK_STARTED'
                    ? 'Starting...'
                    : nextProgress.action === 'WORK_FINISHED'
                      ? 'Finishing...'
                      : 'Updating...'
                ) : nextProgress.label}
              </button>
            </div>
          )}

          {canProof && (
            <form onSubmit={submitProof} className="grid grid-cols-1 md:grid-cols-2 gap-3" aria-busy={proofBusy}>
              <p className="md:col-span-2 text-white/75">
                Work is marked finished. Submit completion notes, proof of work, and invoice details.
              </p>
              <input
                value={proof.invoiceReference}
                onChange={(e) => setProof((prev) => ({ ...prev, invoiceReference: e.target.value }))}
                placeholder="Invoice reference"
                className="p-3 rounded-lg bg-white/5 border border-white/10 text-white"
              />
              <input
                value={proof.invoiceNumber}
                onChange={(e) => setProof((prev) => ({ ...prev, invoiceNumber: e.target.value }))}
                placeholder="Invoice number"
                className="p-3 rounded-lg bg-white/5 border border-white/10 text-white"
              />
              <input
                value={proof.invoiceAmount}
                onChange={(e) => setProof((prev) => ({ ...prev, invoiceAmount: e.target.value }))}
                placeholder="Invoice amount"
                className="p-3 rounded-lg bg-white/5 border border-white/10 text-white"
              />
              <input
                type="datetime-local"
                value={proof.actualCompletedAt}
                onChange={(e) => setProof((prev) => ({ ...prev, actualCompletedAt: e.target.value }))}
                className="p-3 rounded-lg bg-white/5 border border-white/10 text-white"
              />
              <textarea
                value={proof.completionNotes}
                onChange={(e) => setProof((prev) => ({ ...prev, completionNotes: e.target.value }))}
                placeholder="Completion notes"
                rows="3"
                required
                className="md:col-span-2 p-3 rounded-lg bg-white/5 border border-white/10 text-white"
              />
              <input
                value={proof.materialsUsed}
                onChange={(e) => setProof((prev) => ({ ...prev, materialsUsed: e.target.value }))}
                placeholder="Materials used"
                className="p-3 rounded-lg bg-white/5 border border-white/10 text-white"
              />
              <input
                value={proof.followUpRecommendation}
                onChange={(e) => setProof((prev) => ({ ...prev, followUpRecommendation: e.target.value }))}
                placeholder="Follow-up recommendation"
                className="p-3 rounded-lg bg-white/5 border border-white/10 text-white"
              />
              <label className="flex items-center gap-2 text-sm text-white/80">
                <input
                  type="checkbox"
                  checked={proof.additionalWorkRequired}
                  onChange={(e) => setProof((prev) => ({ ...prev, additionalWorkRequired: e.target.checked }))}
                />
                Additional work required
              </label>
              <label className="p-3 rounded-lg bg-white/5 border border-white/10 text-white/80">
                Proof files
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                  onChange={(e) => updateFileList('proofFiles', e.target.files)}
                  className="block w-full mt-2 text-sm"
                />
                {fileNames(proof.proofFiles) && <span className="block mt-2 text-xs text-white/50">{fileNames(proof.proofFiles)}</span>}
              </label>
              <label className="p-3 rounded-lg bg-white/5 border border-white/10 text-white/80">
                Invoice file
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                  onChange={(e) => setProof((prev) => ({ ...prev, invoiceFile: e.target.files?.[0] || null }))}
                  className="block w-full mt-2 text-sm"
                />
                {proof.invoiceFile && <span className="block mt-2 text-xs text-white/50">{proof.invoiceFile.name}</span>}
              </label>
              <label className="p-3 rounded-lg bg-white/5 border border-white/10 text-white/80">
                Before photos
                <input
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={(e) => updateFileList('beforePhotos', e.target.files)}
                  className="block w-full mt-2 text-sm"
                />
              </label>
              <label className="p-3 rounded-lg bg-white/5 border border-white/10 text-white/80">
                After photos
                <input
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={(e) => updateFileList('afterPhotos', e.target.files)}
                  className="block w-full mt-2 text-sm"
                />
              </label>
              <label className="md:col-span-2 p-3 rounded-lg bg-white/5 border border-white/10 text-white/80">
                Supporting documents
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                  onChange={(e) => updateFileList('supportingDocuments', e.target.files)}
                  className="block w-full mt-2 text-sm"
                />
              </label>
              <button disabled={proofBusy} className="md:col-span-2 px-4 py-2 rounded-lg bg-[#AF9B8A] text-white disabled:opacity-50">
                {proofBusy ? 'Completing...' : 'Submit Completion'}
              </button>
            </form>
          )}

          {!canAccept && !canQuote && !canSchedule && !nextProgress && !canProof && (
            <div className="text-white/75 bg-white/5 border border-white/10 rounded-lg p-4">
              {waitingText}
            </div>
          )}
        </section>

        {(job.quote?.attachments?.length > 0 ||
          job.completion?.proofAttachments?.length > 0 ||
          job.completion?.invoiceAttachments?.length > 0) && (
          <section className="mt-6 bg-white/10 border border-white/10 rounded-xl p-6 backdrop-blur-md">
            <h2 className="text-xl font-semibold mb-4">Files</h2>
            <div className="flex flex-wrap gap-2">
              {[
                ...(job.quote?.attachments || []),
                ...(job.completion?.proofAttachments || []),
                ...(job.completion?.invoiceAttachments || []),
              ].map((attachment) => (
                <button
                  key={attachment.id}
                  type="button"
                  onClick={() => openAttachment(attachment.id)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 border border-white/10 hover:bg-[#AF9B8A]/20 text-sm"
                >
                  <FileText className="w-4 h-4" />
                  {attachment.filename}
                </button>
              ))}
            </div>
          </section>
        )}

        <section className="mt-6 bg-white/10 border border-white/10 rounded-xl p-6 backdrop-blur-md">
          <h2 className="text-xl font-semibold mb-4">Activity</h2>
          <ul className="space-y-3 text-sm text-white/75">
            {(job.activities || []).length === 0 ? (
              <li>No updates yet.</li>
            ) : (
              (job.activities || []).map((activity, index) => (
                <li key={`${activity.type}-${activity.createdAt}-${index}`} className="border-b border-white/10 pb-3 last:border-b-0">
                  <p className="text-white">{activity.title}</p>
                  {activity.description && <p>{activity.description}</p>}
                  <p className="text-white/40">{formatDateTime(activity.createdAt, '')}</p>
                </li>
              ))
            )}
          </ul>
        </section>

        <section className="mt-6 bg-white/10 border border-white/10 rounded-xl p-6 backdrop-blur-md">
          <h2 className="text-xl font-semibold mb-4">Contractor preferences</h2>
          <form onSubmit={savePreference} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="md:col-span-2 flex items-start gap-3 text-sm text-white/80">
              <input
                type="checkbox"
                checked={preference.futureJobNotifications}
                onChange={(e) => setPreference((prev) => ({ ...prev, futureJobNotifications: e.target.checked }))}
                className="mt-1"
              />
              <span>I would like to be notified about future jobs in my area from other Beem landlords.</span>
            </label>
            <input
              value={preference.serviceArea}
              onChange={(e) => setPreference((prev) => ({ ...prev, serviceArea: e.target.value }))}
              placeholder="Service area or postcode coverage"
              className="p-3 rounded-lg bg-white/5 border border-white/10 text-white"
            />
            <button disabled={preferenceBusy} className="px-4 py-2 rounded-lg bg-[#AF9B8A] text-white disabled:opacity-50">
              {preferenceBusy ? 'Saving...' : 'Save Preference'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
